// GroupInfoOverlay.js
import { useRef, useState, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import { resizeImage } from "../utils/resizeImage";
import { useSystemMessage, SYSTEM_EVENTS } from "../hooks/useSystemMessage";

export default function GroupInfoOverlay({
  open,
  onClose,
  group,
  members = [],
  myChatId,
  socket,
  setSelectedItem,
  uploadImage,
  inviteLink,
  users = [],
  setMessages,
  setGroups, 
  getGroupKey,
  uuid,
}) {
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [addQuery, setAddQuery] = useState("");
  const [pickedUsers, setPickedUsers] = useState(new Set());

  const [checkingFollow, setCheckingFollow] = useState(false);
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const me = members.find((m) => String(m.chatId) === String(myChatId));
  const amAdmin = me?.isAdmin || me?.isSuperAdmin;
  const myName = me?.userName || String(myChatId);
  // ── System message hook ───────────────────────────────────
  const { sendSystemMessage } = useSystemMessage({
    socket,
    myChatId,
      myName,  
    setMessages,
    getGroupKey,
     setGroups,
    uuid,
  });

  // ── useMemo MUST be before the early return to satisfy Rules of Hooks ──────
  const memberChatIds = new Set(members.map((m) => String(m.chatId)));

  const filteredContacts = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    return (users || []).filter((u) => {
      if (String(u.chatId) === String(myChatId)) return false;
      if (memberChatIds.has(String(u.chatId))) return false;
      if (!q) return true;
      const name = (u.username || u.displayName || String(u.chatId)).toLowerCase();
      return name.includes(q);
    });
  }, [users, addQuery, members, myChatId]);

  // ── FOLLOW-CHECK RESULT ─────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const onFollowCheckResult = ({ targetChatId, canChat }) => {
      setCheckingFollow(false);

      if (String(targetChatId) !== String(selectedMember?.chatId)) {
        return;
      }

      if (canChat) {
        setShowMemberModal(false);
        onClose();

        setSelectedItem({
          type: "user",
          chatId: selectedMember.chatId,
          username:
            selectedMember.userName ||
            selectedMember.username ||
            "@User",
        });

        return;
      }

      setShowFollowModal(true);
    };

    socket.on("group:check-user-follow:result", onFollowCheckResult);

    return () => {
      socket.off("group:check-user-follow:result", onFollowCheckResult);
    };
  }, [socket, selectedMember, onClose, setSelectedItem]);

  // ── FOLLOW ACTION ────────────────────────────────────────────
  const handleFollowUser = () => {
    if (!socket || !selectedMember?.chatId || followLoading) {
      return;
    }

    setFollowLoading(true);

    socket.emit("feed-follow-user", {
      targetChatId: selectedMember.chatId,
    });
  };

  // ── FOLLOW SUCCESS → OPEN DM ─────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const onFollowSuccess = ({ targetChatId }) => {
      if (String(targetChatId) !== String(selectedMember?.chatId)) {
        return;
      }

      setFollowLoading(false);
      setShowFollowModal(false);
      setShowMemberModal(false);
      onClose();

      setSelectedItem({
        type: "user",
        chatId: selectedMember.chatId,
        username:
          selectedMember.userName ||
          selectedMember.username ||
          "@User",
      });
    };

    socket.on("feed-follow-success", onFollowSuccess);

    return () => {
      socket.off("feed-follow-success", onFollowSuccess);
    };
  }, [socket, selectedMember, onClose, setSelectedItem]);

  if (!open || !group) return null;

  const isMe = (member) => String(member.chatId) === String(myChatId);
  const canRemoveMember = (member) => !member.isSuperAdmin && !isMe(member);

  const requireAdmin = (cb) => {
    if (!amAdmin) { toast.info("Only admins can perform this action"); return; }
    cb();
  };

  const copyGroupLink = async () => {
    if (!inviteLink) { toast.error("No invite link available"); return; }
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const openDmWithMember = () => {
    if (!selectedMember) return;

    if (!socket || !selectedMember.chatId || checkingFollow) {
      return;
    }

    setCheckingFollow(true);

    socket.emit("group:check-user-follow", {
      targetChatId: selectedMember.chatId,
    });
  };

  const togglePick = (chatId) => {
    setPickedUsers((prev) => {
      const next = new Set(prev);
      next.has(String(chatId)) ? next.delete(String(chatId)) : next.add(String(chatId));
      return next;
    });
  };

  const handleAddMembers = () => {
    if (!pickedUsers.size) { toast.error("Select at least one contact"); return; }
    pickedUsers.forEach((chatId) => {
      const u = (users || []).find((u) => String(u.chatId) === String(chatId));
      const resolvedName = u?.username || u?.displayName || u?.userName || String(chatId);
      socket.emit("group-add-member", {
        groupId: group.groupId,
        adminChatId: myChatId,
        newChatId: chatId,
        userName: resolvedName,
      });
      // ── system message: actor added target ────────────────
      sendSystemMessage({
        groupId: group.groupId,
        type: SYSTEM_EVENTS.MEMBER_ADDED,
        actorName: myName,
        targetName: resolvedName,
      });
    });
    toast.success(`${pickedUsers.size} member${pickedUsers.size > 1 ? "s" : ""} added`);
    setPickedUsers(new Set());
    setAddQuery("");
    setShowAddModal(false);
  };

const resolveMemberName = (member) =>
  member?.userName || member?.username || member?.displayName || String(member?.chatId || "User");

const promoteAdmin = () => {
  socket.emit("group-make-admin", {
    groupId: group.groupId,
    adminChatId: myChatId,
    targetChatId: selectedMember.chatId,
  });
  sendSystemMessage({
    groupId:     group.groupId,
    type:        SYSTEM_EVENTS.ADMIN_PROMOTED,
    actorName:   myName,
    targetName:  resolveMemberName(selectedMember),
    targetChatId: selectedMember.chatId,
  });
  toast.success("Admin promoted");
  setShowMemberModal(false);
};

const demoteAdmin = () => {
  socket.emit("group-demote-admin", {
    groupId: group.groupId,
    adminChatId: myChatId,
    targetChatId: selectedMember.chatId,
  });
  sendSystemMessage({
    groupId:     group.groupId,
    type:        SYSTEM_EVENTS.ADMIN_DEMOTED,
    actorName:   myName,
    targetName:  resolveMemberName(selectedMember),
    targetChatId: selectedMember.chatId,
  });
  toast.info(isMe(selectedMember) ? "You left the admin role" : "Admin dismissed");
  setShowMemberModal(false);
};
  const removeMember = () => {
    if (selectedMember.isSuperAdmin) { toast.error("Super admin cannot be removed"); return; }
    socket.emit("group-remove-member", {
      groupId: group.groupId,
      adminChatId: myChatId,
      removeChatId: selectedMember.chatId,
    });
    sendSystemMessage({
      groupId: group.groupId,
      type: SYSTEM_EVENTS.MEMBER_REMOVED,
      actorName: myName,
      targetName: selectedMember.userName,
    });
    toast.info("Member removed");
    setShowMemberModal(false);
  };

  return (
    <>
      {/* ═══════════════════════════════════════════════════════════
          GROUP INFO PANEL (right-side drawer)
      ═══════════════════════════════════════════════════════════ */}
      <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
        <div
          className="h-full w-full sm:max-w-sm bg-white dark:bg-neutral-900 flex flex-col"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          {/* HEADER */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800"
            style={{ paddingTop: "max(12px, env(safe-area-inset-top, 12px))" }}
          >
            <h2 className="text-base sm:text-lg font-semibold">Group Info</h2>
            <button
              onClick={onClose}
              className="text-2xl w-9 h-9 flex items-center justify-center touch-manipulation text-neutral-500 hover:text-neutral-800 dark:hover:text-white"
            >
              ×
            </button>
          </div>

          {/* SCROLLABLE BODY */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* GROUP AVATAR + NAME */}
            <div className="flex flex-col items-center px-6 py-6">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const resized = await resizeImage(file, { maxSize: 512, quality: 0.8 });
                    const previewUrl = URL.createObjectURL(resized);
                    setLocalPreview(previewUrl);
                    const uploadedUrl = await uploadImage(resized, setUploadProgress);
                    socket.emit("group-update-avatar", {
                      groupId: group.groupId,
                      chatId: myChatId,
                      imageUrl: uploadedUrl,
                    });
                    toast.success("Group avatar updated");
                  } catch (err) {
                    console.error(err);
                    toast.error("Failed to upload image");
                  }
                }}
              />

              <div
                onClick={() => requireAdmin(() => fileInputRef.current?.click())}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-emerald-600 text-white flex items-center justify-center text-3xl sm:text-4xl cursor-pointer overflow-hidden touch-manipulation"
              >
                {localPreview ? (
                  <img src={localPreview} alt="Group avatar" className="w-full h-full object-cover" />
                ) : group?.groupImageUrl ? (
                  <img src={group.groupImageUrl} alt="Group avatar" className="w-full h-full object-cover" />
                ) : (
                  <span aria-label="Group">👥</span>
                )}
              </div>

              <div className="mt-4 text-lg sm:text-xl font-semibold text-center">{group.name}</div>
              <div className="mt-1 text-sm text-neutral-500">
                {members.length} {members.length === 1 ? "member" : "members"}
              </div>
            </div>

            {/* ADMIN ACTIONS */}
            {amAdmin && (
              <div className="px-4 space-y-2 pb-2">
                <button
                  className="w-full px-3 py-3 sm:py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg text-sm text-left touch-manipulation hover:bg-neutral-200 dark:hover:bg-neutral-700"
                  onClick={() => { setPickedUsers(new Set()); setAddQuery(""); setShowAddModal(true); }}
                >
                  ➕ Add Member
                </button>
                <button
                  className="w-full rounded-lg px-3 py-3 sm:py-2 bg-neutral-100 dark:bg-neutral-800 text-sm text-left touch-manipulation hover:bg-neutral-200 dark:hover:bg-neutral-700"
                  onClick={() => {
                    socket.emit("group-generate-link", { groupId: group.groupId, adminChatId: myChatId });
                    sendSystemMessage({
                      groupId: group.groupId,
                      type: SYSTEM_EVENTS.INVITE_GENERATED,
                      actorName: myName,
                    });
                    toast.success("New invite link generated");
                  }}
                >
                  🔗 Generate Invite Link
                </button>
                <button
                  className="w-full rounded-lg px-3 py-3 sm:py-2 bg-neutral-100 dark:bg-neutral-800 text-sm text-left touch-manipulation hover:bg-neutral-200 dark:hover:bg-neutral-700"
                  onClick={() => {
                    socket.emit("group-revoke-link", { groupId: group.groupId, adminChatId: myChatId });
                    sendSystemMessage({
                      groupId: group.groupId,
                      type: SYSTEM_EVENTS.INVITE_REVOKED,
                      actorName: myName,
                    });
                    toast.info("Invite link revoked");
                  }}
                >
                  🚫 Revoke Invite Link
                </button>
                {inviteLink && (
                  <button
                    className="w-full rounded-lg px-3 py-3 sm:py-2 bg-neutral-100 dark:bg-neutral-800 text-sm text-left touch-manipulation hover:bg-neutral-200 dark:hover:bg-neutral-700"
                    onClick={copyGroupLink}
                  >
                    📋 Copy Invite Link
                  </button>
                )}
              </div>
            )}

            {/* MEMBER LIST */}
            <div className="border-t border-neutral-100 dark:border-neutral-800 mt-2">
              <div className="px-4 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                Members
              </div>
              {members.map((m) => (
                <div
                  key={m.chatId}
                  onClick={() => { setSelectedMember(m); setShowMemberModal(true); }}
                  className="px-4 py-3.5 sm:py-3 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 touch-manipulation flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-lg shrink-0 overflow-hidden">
                    {m.profileImageUrl ? (
                      <img src={m.profileImageUrl} alt={m.userName} className="w-full h-full object-cover" />
                    ) : (
                      <span>👤</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {m.userName}
                      {m.isSuperAdmin && <span className="ml-2 text-xs text-red-500">Super Admin</span>}
                      {!m.isSuperAdmin && m.isAdmin && <span className="ml-2 text-xs text-blue-500">Admin</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* FOOTER ACTIONS */}
            <div className="px-4 py-4 mt-2 border-t border-neutral-200 dark:border-neutral-700 space-y-2">
              <button
                className="w-full rounded-lg px-3 py-3 sm:py-2 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-sm touch-manipulation hover:bg-red-200"
                onClick={() => setShowExitModal(true)}
              >
                🚪 Exit Group
              </button>
              <button
                className="w-full rounded-lg px-3 py-3 sm:py-2 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-sm touch-manipulation hover:bg-amber-200"
                onClick={() => toast.info("Report submitted")}
              >
                ⚠️ Report Group
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          ADD MEMBER MODAL
      ═══════════════════════════════════════════════════════════ */}
      {showAddModal && (
        <>
          <div
            className="fixed inset-0 z-[61] bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAddModal(false)}
          />
          <div
            className={[
              "fixed z-[62] flex flex-col overflow-hidden",
              "bg-white dark:bg-neutral-900",
              "inset-0",
              "sm:inset-auto sm:top-1/2 sm:left-1/2",
              "sm:-translate-x-1/2 sm:-translate-y-1/2",
              "sm:w-[460px] sm:max-h-[85vh]",
              "sm:rounded-2xl",
              "sm:shadow-2xl sm:border sm:border-neutral-200 sm:dark:border-neutral-700",
            ].join(" ")}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Header ── */}
            <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex items-center justify-center w-9 h-9 rounded-full text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-manipulation"
                aria-label="Close"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-base text-neutral-900 dark:text-neutral-100 leading-tight">
                  Add Members
                </p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                  {pickedUsers.size > 0 ? `${pickedUsers.size} selected` : "Select contacts to add"}
                </p>
              </div>
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 flex-shrink-0">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
              </div>
            </div>

            {/* ── Search bar ── */}
            <div className="flex-shrink-0 px-4 py-2.5 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-2 bg-neutral-100 dark:bg-neutral-800 rounded-full px-4 py-2.5">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="text-neutral-400 flex-shrink-0">
                  <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
                <input
                  className="flex-1 bg-transparent text-sm outline-none text-neutral-900 dark:text-neutral-100 placeholder-neutral-400"
                  placeholder="Search contacts…"
                  value={addQuery}
                  onChange={(e) => setAddQuery(e.target.value)}
                />
                {addQuery.length > 0 && (
                  <button onClick={() => setAddQuery("")} className="text-neutral-400 hover:text-neutral-600 touch-manipulation">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* ── Contact list ── */}
            <div
              className="flex-1 overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {filteredContacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 gap-2 text-neutral-400 text-sm">
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" className="opacity-40">
                    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </svg>
                  {addQuery ? "No contacts found" : "All contacts are already in this group"}
                </div>
              ) : (
                <ul>
                  {filteredContacts.map((u) => {
                    const id = String(u.chatId);
                    const isChecked = pickedUsers.has(id);
                    const label = u.username || u.displayName || id;
                    return (
                      <li key={id}>
                        <button
                          className={[
                            "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors touch-manipulation",
                            isChecked
                              ? "bg-green-50 dark:bg-green-900/20"
                              : "hover:bg-neutral-50 dark:hover:bg-neutral-800/60 active:bg-neutral-100",
                          ].join(" ")}
                          onClick={() => togglePick(id)}
                        >
                          <div className="relative w-11 h-11 flex-shrink-0">
                            {u.profileImageUrl ? (
                              <img src={u.profileImageUrl} alt={label} className="w-11 h-11 rounded-full object-cover" />
                            ) : (
                              <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl" style={{ background: "#e0f2fe" }}>
                                👤
                              </div>
                            )}
                            {u.online && (
                              <span className="absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-neutral-900 bg-green-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate text-neutral-900 dark:text-neutral-100">{label}</p>
                          </div>
                          <div
                            className={[
                              "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all",
                              isChecked ? "bg-green-600 border-green-600 scale-110" : "border-neutral-300 dark:border-neutral-600",
                            ].join(" ")}
                          >
                            {isChecked && (
                              <svg viewBox="0 0 24 24" width="13" height="13" fill="white">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                              </svg>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* ── Footer ── */}
            <div
              className="flex-shrink-0 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3"
              style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}
            >
              {pickedUsers.size > 0 && (
                <div
                  className="flex gap-2 overflow-x-auto pb-2 mb-3"
                  style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
                >
                  {Array.from(pickedUsers).map((chatId) => {
                    const u = (users || []).find((u) => String(u.chatId) === String(chatId));
                    const label = u?.username || u?.displayName || chatId;
                    return (
                      <button
                        key={chatId}
                        onClick={() => togglePick(chatId)}
                        className="flex-shrink-0 flex items-center gap-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full pl-2 pr-3 py-1.5 text-xs font-medium touch-manipulation hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300 transition-colors group"
                      >
                        <span className="text-sm leading-none">👤</span>
                        <span className="max-w-[80px] truncate">{label}</span>
                        <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor" className="opacity-60 group-hover:opacity-100">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 rounded-full text-sm font-medium text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  disabled={!pickedUsers.size}
                  onClick={handleAddMembers}
                  className={[
                    "flex-[2] py-2.5 rounded-full text-sm font-semibold text-white transition-all touch-manipulation flex items-center justify-center gap-2 shadow-sm",
                    pickedUsers.size
                      ? "bg-green-600 hover:bg-green-700 active:bg-green-800"
                      : "bg-neutral-300 dark:bg-neutral-700 cursor-not-allowed opacity-60",
                  ].join(" ")}
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                    <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                  Add{pickedUsers.size > 0 ? ` (${pickedUsers.size})` : ""}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* MEMBER ACTION MODAL */}
      {showMemberModal && selectedMember && (
        <Modal onClose={() => setShowMemberModal(false)}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xl shrink-0 overflow-hidden">
              {selectedMember.profileImageUrl ? (
                <img src={selectedMember.profileImageUrl} alt={selectedMember.userName} className="w-full h-full object-cover" />
              ) : (
                <span>👤</span>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-sm">{selectedMember.userName}</h3>
              {selectedMember.isSuperAdmin && <p className="text-xs text-red-500">Super Admin</p>}
              {!selectedMember.isSuperAdmin && selectedMember.isAdmin && <p className="text-xs text-blue-500">Admin</p>}
            </div>
          </div>
        {!isMe(selectedMember) && (
            <button
              onClick={openDmWithMember}
              disabled={checkingFollow}
              className="w-full py-3 sm:py-2 bg-green-600 text-white rounded-lg mb-2 text-sm touch-manipulation hover:bg-green-700"
            >
              💬 Send Message
            </button>
          )}
          {amAdmin && !selectedMember.isAdmin && (
            <button onClick={promoteAdmin} className="w-full py-3 sm:py-2 bg-blue-600 text-white rounded-lg mb-2 text-sm touch-manipulation hover:bg-blue-700">
              Make Admin
            </button>
          )}
          {amAdmin && selectedMember.isAdmin && !selectedMember.isSuperAdmin && (
            <button onClick={demoteAdmin} className="w-full py-3 sm:py-2 bg-amber-500 text-white rounded-lg mb-2 text-sm touch-manipulation hover:bg-amber-600">
              {isMe(selectedMember) ? "Leave Admin Role" : "Dismiss as Admin"}
            </button>
          )}
          {amAdmin && canRemoveMember(selectedMember) && (
            <button onClick={removeMember} className="w-full py-3 sm:py-2 bg-red-600 text-white rounded-lg text-sm touch-manipulation hover:bg-red-700">
              Remove from Group
            </button>
          )}
        </Modal>
      )}

      {/* FOLLOW-TO-CHAT MODAL */}
      {showFollowModal && selectedMember && (
        <Modal
          onClose={() => {
            if (followLoading) return;
            setShowFollowModal(false);
          }}
        >
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-2xl">
              👤
            </div>

            <h3 className="font-semibold text-base mb-2">
              Follow {resolveMemberName(selectedMember)} to chat
            </h3>

            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-5">
              You need to follow this user before you can
              start a private conversation.
            </p>

            <button
              onClick={handleFollowUser}
              disabled={followLoading}
              className="w-full py-3 sm:py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-60"
            >
              {followLoading ? "Following..." : "Follow & Chat"}
            </button>
          </div>
        </Modal>
      )}

      {/* EXIT GROUP MODAL */}
      {showExitModal && (
        <Modal onClose={() => setShowExitModal(false)}>
          <h3 className="font-semibold mb-3 text-center">Leave this group?</h3>
          <div className="flex gap-3">
            <button
              className="flex-1 py-3 sm:py-2 rounded-lg bg-neutral-200 dark:bg-neutral-700 text-sm touch-manipulation"
              onClick={() => setShowExitModal(false)}
            >
              No
            </button>
            <button
              className="flex-1 py-3 sm:py-2 rounded-lg bg-red-600 text-white text-sm touch-manipulation hover:bg-red-700"
              onClick={() => {
                socket.emit("group-exit", { groupId: group.groupId, chatId: myChatId });
                // MEMBER_LEFT is sent by the groupSocket exit handler, not here,
                // because the leaving user loses access to the group key after exit.
                toast.info("You left the group");
                setSelectedItem(null);
                setShowExitModal(false);
                onClose();
              }}
            >
              Yes
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-[70]">
      <div
        className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl p-4 w-full sm:w-80"
        style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom, 16px))" }}
      >
        {children}
        <button onClick={onClose} className="mt-3 text-sm text-neutral-500 w-full py-2 touch-manipulation">
          Cancel
        </button>
      </div>
    </div>
  );
}